import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';

import { selectUser } from 'store';

import { FLOW_TYPES } from 'constants/onboarding';
import { useAppInstallPrompts } from 'hooks/useAppInstallPrompts/useAppInstallPrompts';
import { useAppSelector, useQuery } from 'hooks';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { FeatureFlag, FlowTypes, PlanTypes } from 'utils/enums';
import { PathName } from './paths';

const Router = () => {
  const prevLocation = useLocation();
  const { accessToken } = useAppSelector(selectUser);
  const query = useQuery();
  const isSignUp = useRouteMatch(PathName.SignUp);
  const isVerifyEmail = !!useRouteMatch(PathName.VerifyEmail);
  const isVerifyIdentity = !!useRouteMatch(PathName.VerifyIdentity);

  const unprotectedRoutes = [
    PathName.Login,
    PathName.SignUp,
    PathName.ForgotPassword,
    PathName.CreateNewPassword,
    PathName.CreateFreeAccount,
    PathName.VerifyEmail,
    PathName.VerifyIdentity,
    PathName.ConfirmAccount,
    PathName.CreateAppointment,
    PathName.Join
  ];

  const isNotProtectedRoute =
    unprotectedRoutes.some((route) => useRouteMatch(route)) &&
    window.location.pathname !== PathName.Home;

  // Mirror the two early-return conditions below so the app-install-prompt effect can be called
  // unconditionally (required by the Rules of Hooks) while still matching, byte for byte, when
  // the pre-effect `!isSignUp && appendAppBanner()` call used to run: never on a render that is
  // about to redirect away.
  const willRedirectUnauthenticated = !isNotProtectedRoute && !accessToken;
  const willRedirectAuthenticated =
    isNotProtectedRoute &&
    !!accessToken &&
    !query.get('login-token') &&
    !query.get('action') &&
    !isVerifyEmail &&
    !isVerifyIdentity;

  useAppInstallPrompts(!isSignUp && !willRedirectUnauthenticated && !willRedirectAuthenticated);

  const navigateTo = (to: string) => {
    const queryString = query.toString();
    if (queryString.length > 0) {
      to += (to.includes('?') ? '&' : '?') + queryString;
    }
    return <Navigate to={to} replace />;
  };

  if (willRedirectUnauthenticated) {
    const redirectTo =
      prevLocation.pathname !== PathName.Home
        ? `${PathName.Login}?redirectTo=${prevLocation.pathname}`
        : PathName.Login;
    return navigateTo(redirectTo);
  }

  if (isNotProtectedRoute && accessToken && !query.get('login-token') && !query.get('action')) {
    const redirectTo = query.get('redirectTo') || PathName.Home;
    query.delete('redirectTo');
    query.delete('token');
    if (!isVerifyEmail && !isVerifyIdentity) return navigateTo(redirectTo);
  }

  return <Outlet />;
};

export default Router;

export const SignupRouter = () => {
  const { accessToken } = useAppSelector(selectUser);
  const { search } = useLocation();
  const { flow = FlowTypes.WeightManagementFlow, plan = PlanTypes.WeightManagement } = useParams();
  let patchedFlow = FLOW_TYPES.hasOwnProperty(flow) ? flow : FlowTypes.WeightManagementFlow;

  const isDisabledInternalSignups = useFlag(FeatureFlag.DisableInternalSignups);

  const redirectPath = accessToken
    ? PathName.Login
    : !FLOW_TYPES.hasOwnProperty(flow) || isDisabledInternalSignups
      ? PathName.CreateAppointment
      : `${PathName.SignUp}/${plan}/${patchedFlow}/${FLOW_TYPES[patchedFlow as FlowTypes][0] + search}`;

  return <Navigate to={redirectPath} replace />;
};
